import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { CurrencyPipe, PercentPipe } from "@angular/common";
import { PromotionValidators } from "../promotion.validator";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PromotionInfoData, PromotionType } from "../promotion.model";
import { BasePromotionService } from "../base-promotion.service";
import { BrandingService } from "../../../core/branding/branding.service";
import { CompanyBranding } from "../../branding/branding.model";
import { FeeType } from "../../application/application.model";
import {
  AccountTransferData
} from "../../account-transfer/account-transfer.model";

@UntilDestroy()
@Component({
    selector: 'ifc-promo-code',
    templateUrl: './promo-code.component.html',
    styleUrls: ['./promo-code.component.scss'],
    standalone: false
})
export class PromoCodeComponent implements OnInit {
  @Input()
  data: AccountTransferData;
  form: UntypedFormGroup;
  brandingName: string;
  promoCodeInvalid = false;
  promotionInfo: PromotionInfoData;

  @Output()
  promotionApplied = new EventEmitter<PromotionInfoData>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private currencyPipe: CurrencyPipe,
    private percentPipe: PercentPipe,
    private promotionService: BasePromotionService,
    private brandingService: BrandingService
  ) {
    this.form = this.formBuilder.group({
      promoCode: [undefined, PromotionValidators.promoCodeValidator(this.promoCodeInvalid)]
    });
  }

  ngOnInit(): void {
    this.initBrandingInfo();
  }

  applyPromoCode(): void {
    const promoCodeToApply = this.form.controls.promoCode.value;

    this.promotionService
      .getByPromoCode(promoCodeToApply, this.data?.loan?.id, this.data?.type)
      .pipe(untilDestroyed(this))
      .subscribe(promotionInfo => {
        if (promotionInfo) {
          this.promoCodeInvalid = false;
          this.promotionInfo = promotionInfo;
          this.form.controls.promoCode.setValidators(PromotionValidators.promoCodeValidator(this.promoCodeInvalid));
          this.form.controls.promoCode.updateValueAndValidity();
          this.form.controls.promoCode.setValue(undefined);
        } else {
          this.promoCodeInvalid = true;
          this.promotionInfo = null;
          this.form.controls.promoCode.setValidators(PromotionValidators.promoCodeValidator(this.promoCodeInvalid));
          this.form.controls.promoCode.updateValueAndValidity();
        }

        this.promotionApplied.emit(this.promotionInfo);
      })
  }

  removeAppliedProductCode(): void {
    this.promoCodeInvalid = false;
    this.promotionInfo = null;
    this.promotionApplied.emit(this.promotionInfo);
  }

  private initBrandingInfo(): void {
    this.brandingService.getBranding$()
      .pipe(untilDestroyed(this))
      .subscribe((branding: CompanyBranding) => {
        this.brandingName = branding?.dba ?? branding?.name ?? 'Idea Financial';
      })
  }

  get promotionInfoDetails(): string {
    if (!this.promotionInfo)
      return '';

    if (this.promotionInfo.promotionType === PromotionType.DrawFeeOverride && this.promotionInfo.reduceType === FeeType.Flat)
      return 'Draw fee amount will be overridden with ' + this.currencyPipe.transform(this.promotionInfo.reduceValue, 'USD', 'symbol', '1.2-2') + ' if it is lower than the original one';

    else if (this.promotionInfo.promotionType === PromotionType.DrawFeeOverride && this.promotionInfo.reduceType === FeeType.Percentage)
      return 'Draw fee amount will be overridden with ' + this.percentPipe.transform(this.promotionInfo.reduceValue / 100, '1.2-2') + ' if it is lower than the original one';

    if (this.promotionInfo.promotionType === PromotionType.DrawFeeReduction && this.promotionInfo.reduceType === FeeType.Flat)
      return 'Draw fee will be reduced by maximum ' + this.currencyPipe.transform(this.promotionInfo.reduceValue, 'USD', 'symbol', '1.2-2');

    else if (this.promotionInfo.promotionType === PromotionType.DrawFeeReduction && this.promotionInfo.reduceType === FeeType.Percentage)
      return 'Draw fee rate will be reduced by maximum ' + this.percentPipe.transform(this.promotionInfo.reduceValue / 100, '1.2-2');

    if (this.promotionInfo.promotionType === PromotionType.ReAmortization)
      return 'Loan will be re-amortized after partial pre-payment is made';

    if (this.promotionInfo.promotionType === PromotionType.PayoffInterestReduction && this.promotionInfo.reduceType === FeeType.Flat)
      return 'Payoff interest will be reduced by maximum ' + this.currencyPipe.transform(this.promotionInfo.reduceValue, 'USD', 'symbol', '1.2-2');

    else if (this.promotionInfo.promotionType === PromotionType.PayoffInterestReduction && this.promotionInfo.reduceType === FeeType.Percentage)
      return 'Payoff interest will be reduced by ' + this.percentPipe.transform(this.promotionInfo.reduceValue / 100, '1.2-2');
  }
}


